import { useCallback, useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/nextjs';
import debounce from 'lodash/debounce';
import { VairUser } from 'constants/payloads/vair/playerSearch';
import {
  GetUserProfileByNameSearchQuery,
  useGetUserProfileByNameSearchLazyQuery,
} from 'types/generated/client';
import { getProfileImageUrlOrPlaceholder } from 'utils/shared/user/getProfileImageUrlOrPlaceholder';
import { parseVairSportRating } from 'utils/shared/vair/parsePrimaryRating';
import CloseIcon from 'svg/CloseIcon';
import Location from 'svg/Location';
import PlusPlain from 'svg/PlusPlain';
import User from 'svg/User';
import SearchInput from 'screens/TournamentAdmin/components/SearchInput';
import { Button, ButtonText } from 'components/Button';
import Modal from 'components/modals/Modal';
import classNames from 'styles/utils/classNames';
import type { UserNeedingPartner } from './UserEventList/utils';

type DatabaseUser = GetUserProfileByNameSearchQuery['userProfiles'][number];

const MIN_SEARCH_LENGTH = 2;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  usersNeedingPartner: UserNeedingPartner[];
  vairUsersMap?: Record<string, VairUser>;
  onSelectPartner: (user: UserNeedingPartner) => void;
  onInvitePartner: () => void;
  onInviteDatabaseUser: (user: DatabaseUser) => void;
  initialSelectedUserId?: string | null;
  initialSelectedName?: string | null;
  isLoading?: boolean;
}

function getPlayerDisplayName(user: UserNeedingPartner): string {
  return user.userProfile?.fullName || user.userProfile?.preferredName || 'Unknown';
}

function getVairBadgeColors(rating: number | null): { bg: string; text: string } {
  if (rating === null) return { bg: 'bg-gray-100', text: 'text-gray-600' };
  if (rating >= 5.0) return { bg: 'bg-pink-100', text: 'text-pink-700' };
  if (rating >= 4.0) return { bg: 'bg-red-100', text: 'text-red-700' };
  if (rating >= 3.0) return { bg: 'bg-orange-100', text: 'text-orange-700' };
  return { bg: 'bg-green-100', text: 'text-green-700' };
}

function getGenderLabel(gender: string | null | undefined): string | null {
  if (!gender) return null;
  const g = gender.trim().toUpperCase();
  if (g === 'MALE' || g === 'M') return 'M';
  if (g === 'FEMALE' || g === 'F') return 'F';
  return gender.charAt(0).toUpperCase();
}

function getLocationDisplay(vairUser: VairUser | undefined): string | null {
  if (!vairUser) return null;
  const parts = [vairUser.city, vairUser.state, vairUser.country].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
}

export default function ModalSearchPartner({
  isOpen,
  onClose,
  usersNeedingPartner,
  vairUsersMap,
  onSelectPartner,
  onInvitePartner,
  onInviteDatabaseUser,
  initialSelectedUserId,
  initialSelectedName,
  isLoading,
}: Props) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedDatabaseUser, setSelectedDatabaseUser] = useState<DatabaseUser | null>(null);
  const [databaseResults, setDatabaseResults] = useState<DatabaseUser[]>([]);

  const [getUserProfileByNameSearch, { loading: isSearchingDatabase }] =
    useGetUserProfileByNameSearchLazyQuery();

  const eventUserIds = useMemo(() => {
    const ids = new Set<string>();
    for (const u of usersNeedingPartner) {
      if (u.userId) ids.add(u.userId);
      if (u.userProfile?.id) ids.add(u.userProfile.id);
    }
    return ids;
  }, [usersNeedingPartner]);

  const fetchDatabaseUsers = async (searchTerm: string) => {
    if (!searchTerm.trim() || searchTerm.trim().length < MIN_SEARCH_LENGTH) {
      setDatabaseResults([]);
      return;
    }

    try {
      const response = await getUserProfileByNameSearch({
        variables: { name: `%${searchTerm}%`, limit: 10 },
      });
      if (response.data?.userProfiles) {
        // Filter out users already shown in the event section
        const filtered = response.data.userProfiles.filter((u) => !eventUserIds.has(u.id));
        setDatabaseResults(filtered);
      }
    } catch (err) {
      captureException(err);
    }
  };

  const debouncedFetchDatabaseUsers = useCallback(debounce(fetchDatabaseUsers, 300), [
    eventUserIds,
  ]);

  useEffect(() => {
    if (searchQuery.trim()) {
      debouncedFetchDatabaseUsers(searchQuery);
    } else {
      setDatabaseResults([]);
    }
  }, [searchQuery, debouncedFetchDatabaseUsers]);

  // Auto-select a database user when results load and we have a pending preselection
  useEffect(() => {
    if (initialSelectedUserId && databaseResults.length > 0 && !selectedDatabaseUser) {
      const match = databaseResults.find((u) => u.id === initialSelectedUserId);
      if (match) {
        setSelectedDatabaseUser(match);
      }
    }
  }, [databaseResults, initialSelectedUserId, selectedDatabaseUser]);

  useEffect(() => {
    if (isOpen) {
      const isEventUser = usersNeedingPartner.some((u) => u.userId === initialSelectedUserId);

      if (isEventUser) {
        setSelectedUserId(initialSelectedUserId ?? null);
        setSelectedDatabaseUser(null);
        setSearchQuery('');
        setDatabaseResults([]);
      } else if (initialSelectedUserId && initialSelectedName) {
        // Database user was previously selected — pre-populate search to find them
        setSelectedUserId(null);
        setSelectedDatabaseUser(null);
        setSearchQuery(initialSelectedName);
      } else {
        setSelectedUserId(null);
        setSelectedDatabaseUser(null);
        setSearchQuery('');
        setDatabaseResults([]);
      }
    }
  }, [isOpen, initialSelectedUserId, initialSelectedName, usersNeedingPartner]);

  const filteredEventUsers = useMemo(() => {
    if (!searchQuery.trim()) return usersNeedingPartner;
    const q = searchQuery.trim().toLowerCase();
    return usersNeedingPartner.filter((u) => {
      const name = getPlayerDisplayName(u).toLowerCase();
      if (name.includes(q)) return true;
      const vairUser = u.userId ? vairUsersMap?.[u.userId] : undefined;
      if (vairUser) {
        const location = getLocationDisplay(vairUser)?.toLowerCase();
        if (location?.includes(q)) return true;
      }
      return false;
    });
  }, [usersNeedingPartner, searchQuery, vairUsersMap]);

  const selectedEventUser = usersNeedingPartner.find((u) => u.userId === selectedUserId);

  const handleClose = () => {
    setSearchQuery('');
    setSelectedUserId(null);
    setSelectedDatabaseUser(null);
    setDatabaseResults([]);
    onClose();
  };

  const handleSelectEventUser = (user: UserNeedingPartner) => {
    const isSelected = selectedUserId === user.userId;
    setSelectedUserId(isSelected ? null : user.userId);
    setSelectedDatabaseUser(null);
  };

  const handleSelectDatabaseUser = (user: DatabaseUser) => {
    const isSelected = selectedDatabaseUser?.id === user.id;
    setSelectedDatabaseUser(isSelected ? null : user);
    setSelectedUserId(null);
  };

  const handleConfirmSelection = () => {
    if (selectedEventUser) {
      onSelectPartner(selectedEventUser);
      setSearchQuery('');
      setSelectedUserId(null);
    } else if (selectedDatabaseUser) {
      onInviteDatabaseUser(selectedDatabaseUser);
      setSearchQuery('');
      setSelectedDatabaseUser(null);
    }
  };

  const hasSelection = !!selectedEventUser || !!selectedDatabaseUser;
  const showDatabaseSection = searchQuery.trim().length > 0;

  return (
    <Modal isOpen={isOpen} handleClose={handleClose} classNameMaxWidth="max-w-[40rem]">
      <div className="flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-color-border-input-lightmode px-6 py-5 dark:border-color-border-input-darkmode lg:px-10">
          <h2 className="typography-product-heading text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            Search players
          </h2>
          <ButtonText onClick={handleClose}>
            <CloseIcon className="h-6 w-6 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary" />
          </ButtonText>
        </div>

        {/* Content */}
        <div className="flex flex-col  px-6 py-6 lg:px-10">
          {/* Search + Invite row */}
          <div className="flex items-center gap-6">
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search"
              className="!max-w-none flex-1"
            />
            <Button
              variant="brand"
              size="sm"
              className="shrink-0 !rounded-full"
              isInline
              iconLeft={<PlusPlain className="h-4 w-4" />}
              onClick={() => {
                onInvitePartner();
              }}
            >
              Invite partner
            </Button>
          </div>

          {/* Divider */}
          <div className="mt-ds-lg h-px  w-full bg-color-border-input-lightmode dark:bg-color-border-input-darkmode" />

          {/* Player list */}
          <div className="-mx-4 flex max-h-[24rem] flex-col gap-2 overflow-y-auto px-4 py-4">
            {/* In this event section */}
            {filteredEventUsers.length > 0 && (
              <>
                <p className="px-2 text-xs font-semibold uppercase tracking-wide text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
                  In this event
                </p>
                {filteredEventUsers.map((user) => {
                  const isSelected = selectedUserId === user.userId;
                  const vairUser = user.userId ? vairUsersMap?.[user.userId] : undefined;
                  const parsed = parseVairSportRating(vairUser?.sport?.pickleball);
                  const ratingDisplay = parsed.formattedValue;
                  const badgeColors = getVairBadgeColors(parsed.value);
                  const location = getLocationDisplay(vairUser);
                  const gender = getGenderLabel(vairUser?.gender);
                  const profileImage = getProfileImageUrlOrPlaceholder({
                    path: user.userProfile?.profileImagePath,
                  });

                  return (
                    <div
                      key={user.userId}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleSelectEventUser(user)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSelectEventUser(user);
                        }
                      }}
                      className={classNames(
                        'flex w-full cursor-pointer items-center gap-2 rounded-lg p-2 text-left outline-none transition-all',
                        isSelected &&
                          'shadow-[0px_0px_0px_2px_#FFF,0px_0px_0px_3px_#B9B9BE,0px_4px_20px_0px_rgba(0,0,0,0.16)]',
                      )}
                    >
                      <img
                        src={profileImage}
                        alt={getPlayerDisplayName(user)}
                        className="h-14 w-14 shrink-0 rounded-full object-cover"
                      />
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <span className="text-base font-semibold leading-tight text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                            {getPlayerDisplayName(user)}
                          </span>
                          {ratingDisplay && (
                            <span
                              className={classNames(
                                'rounded-xl px-1.5 py-0.5 text-xs font-medium',
                                badgeColors.bg,
                                badgeColors.text,
                              )}
                            >
                              VAIR {ratingDisplay}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-col">
                          {location && (
                            <div className="flex items-center gap-1">
                              <Location className="h-4 w-4 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary" />
                              <span className="text-sm leading-[18px] text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                                {location}
                              </span>
                            </div>
                          )}
                          {gender && (
                            <div className="flex items-center gap-1">
                              <User className="h-4 w-4 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary" />
                              <span className="text-sm leading-[18px] text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                                {gender}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            {/* All players (database search) section */}
            {showDatabaseSection && (
              <>
                {filteredEventUsers.length > 0 && (
                  <div className="my-2 h-px w-full bg-color-border-input-lightmode dark:bg-color-border-input-darkmode" />
                )}
                <p className="px-2 text-xs font-semibold uppercase tracking-wide text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
                  All players
                </p>
                {isSearchingDatabase ? (
                  <p className="py-4 text-center text-sm text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
                    Searching...
                  </p>
                ) : databaseResults.length === 0 ? (
                  <p className="py-4 text-center text-sm text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
                    No other players found matching your search.
                  </p>
                ) : (
                  databaseResults.map((user) => {
                    const isSelected = selectedDatabaseUser?.id === user.id;
                    const profileImage = getProfileImageUrlOrPlaceholder({
                      path: user.profileImagePath,
                    });

                    return (
                      <div
                        key={user.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleSelectDatabaseUser(user)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleSelectDatabaseUser(user);
                          }
                        }}
                        className={classNames(
                          'flex w-full cursor-pointer items-center gap-2 rounded-lg p-2 text-left outline-none transition-all',
                          isSelected &&
                            'shadow-[0px_0px_0px_2px_#FFF,0px_0px_0px_3px_#B9B9BE,0px_4px_20px_0px_rgba(0,0,0,0.16)]',
                        )}
                      >
                        <img
                          src={profileImage}
                          alt={user.fullName || ''}
                          className="h-14 w-14 shrink-0 rounded-full object-cover"
                        />
                        <div className="flex flex-col gap-1">
                          <span className="text-base font-semibold leading-tight text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                            {user.fullName || user.username || 'Unknown'}
                          </span>
                          <div className="flex flex-col">
                            {user.cityName && (
                              <div className="flex items-center gap-1">
                                <Location className="h-4 w-4 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary" />
                                <span className="text-sm leading-[18px] text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                                  {user.cityName}
                                </span>
                              </div>
                            )}
                            {getGenderLabel(user.gender) && (
                              <div className="flex items-center gap-1">
                                <User className="h-4 w-4 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary" />
                                <span className="text-sm leading-[18px] text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                                  {getGenderLabel(user.gender)}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </>
            )}

            {/* Empty state when no search and no event users */}
            {filteredEventUsers.length === 0 && !showDatabaseSection && (
              <p className="py-4 text-center text-sm text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
                No players currently need a partner.
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end border-t border-color-border-input-lightmode px-6 py-5 dark:border-color-border-input-darkmode lg:px-10">
          <Button
            variant="primary"
            size="lg"
            isInline
            disabled={!hasSelection}
            onClick={handleConfirmSelection}
            loading={isLoading}
          >
            {selectedDatabaseUser ? 'Invite to event' : 'Select partner'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
